import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

interface MeetingFormProps {
  stepId: number;
  stepTypeId: number;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export default function MeetingForm({ stepId, stepTypeId, onSubmit, onCancel }: MeetingFormProps) {
  const [meeting, setMeeting] = useState({
    name: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    assigned_to_type: 'team',
    due_date: '',
    estimated_hours: 1,
    // Meeting-specific fields
    meeting_date: '',
    meeting_time: '',
    duration_minutes: 60,
    meeting_type: 'virtual', // virtual, in-person, hybrid
    meeting_link: '',
    location: '',
    attendees: [] as string[],
    agenda_items: [] as string[],
    recording_required: false,
    notes: ''
  });

  const [newAttendee, setNewAttendee] = useState('');
  const [newAgendaItem, setNewAgendaItem] = useState('');

  const addAttendee = () => {
    if (newAttendee.trim()) {
      setMeeting({ ...meeting, attendees: [...meeting.attendees, newAttendee.trim()] });
      setNewAttendee('');
    }
  };

  const removeAttendee = (index: number) => {
    setMeeting({
      ...meeting,
      attendees: meeting.attendees.filter((_, i) => i !== index)
    });
  };

  const addAgendaItem = () => {
    if (newAgendaItem.trim()) {
      setMeeting({ ...meeting, agenda_items: [...meeting.agenda_items, newAgendaItem.trim()] });
      setNewAgendaItem('');
    }
  };

  const removeAgendaItem = (index: number) => {
    setMeeting({
      ...meeting,
      agenda_items: meeting.agenda_items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = () => {
    if (!meeting.name) {
      alert('Please enter a meeting title');
      return;
    }
    if (!meeting.meeting_date || !meeting.meeting_time) {
      alert('Please set meeting date and time');
      return;
    }

    // Combine date and time for due_date
    const dueDate = `${meeting.meeting_date}T${meeting.meeting_time}`;

    const config = {
      meeting_date: meeting.meeting_date,
      meeting_time: meeting.meeting_time,
      duration_minutes: meeting.duration_minutes,
      meeting_type: meeting.meeting_type,
      meeting_link: meeting.meeting_link,
      location: meeting.location,
      attendees: meeting.attendees,
      agenda_items: meeting.agenda_items,
      recording_required: meeting.recording_required,
      notes: meeting.notes
    };

    onSubmit({
      step_type_id: stepTypeId,
      name: meeting.name,
      description: meeting.description,
      status: meeting.status,
      priority: meeting.priority,
      assigned_to: meeting.assigned_to,
      assigned_to_type: meeting.assigned_to_type,
      due_date: dueDate,
      estimated_hours: meeting.duration_minutes / 60,
      config: JSON.stringify(config)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Schedule Meeting</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Meeting Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Title *
            </label>
            <input
              type="text"
              value={meeting.name}
              onChange={(e) => setMeeting({ ...meeting, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="e.g., Q4 Planning Review"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={meeting.description}
              onChange={(e) => setMeeting({ ...meeting, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Brief description of the meeting purpose..."
            />
          </div>

          {/* Date, Time, Duration */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">When</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={meeting.meeting_date}
                  onChange={(e) => setMeeting({ ...meeting, meeting_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time *
                </label>
                <input
                  type="time"
                  value={meeting.meeting_time}
                  onChange={(e) => setMeeting({ ...meeting, meeting_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes)
                </label>
                <select
                  value={meeting.duration_minutes}
                  onChange={(e) => setMeeting({ ...meeting, duration_minutes: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>
            </div>
          </div>

          {/* Meeting Type & Location */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Where</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Type
                </label>
                <select
                  value={meeting.meeting_type}
                  onChange={(e) => setMeeting({ ...meeting, meeting_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="virtual">Virtual (Online)</option>
                  <option value="in-person">In-Person</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              {(meeting.meeting_type === 'virtual' || meeting.meeting_type === 'hybrid') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meeting Link
                  </label>
                  <input
                    type="url"
                    value={meeting.meeting_link}
                    onChange={(e) => setMeeting({ ...meeting, meeting_link: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://zoom.us/... or https://meet.google.com/..."
                  />
                </div>
              )}

              {(meeting.meeting_type === 'in-person' || meeting.meeting_type === 'hybrid') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={meeting.location}
                    onChange={(e) => setMeeting({ ...meeting, location: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Conference Room A, 123 Main St, etc."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Attendees */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Attendees</h4>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAttendee}
                  onChange={(e) => setNewAttendee(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addAttendee()}
                  className="flex-1 px-3 py-2 border rounded-lg"
                  placeholder="Enter name or email..."
                />
                <button
                  onClick={addAttendee}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {meeting.attendees.length > 0 && (
                <div className="space-y-1">
                  {meeting.attendees.map((attendee, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm">{attendee}</span>
                      <button
                        onClick={() => removeAttendee(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Agenda */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Agenda</h4>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAgendaItem}
                  onChange={(e) => setNewAgendaItem(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addAgendaItem()}
                  className="flex-1 px-3 py-2 border rounded-lg"
                  placeholder="Add agenda item..."
                />
                <button
                  onClick={addAgendaItem}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {meeting.agenda_items.length > 0 && (
                <ol className="space-y-1 list-decimal list-inside">
                  {meeting.agenda_items.map((item, index) => (
                    <li key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm">{item}</span>
                      <button
                        onClick={() => removeAgendaItem(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Additional Options */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="recording_required"
                checked={meeting.recording_required}
                onChange={(e) => setMeeting({ ...meeting, recording_required: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="recording_required" className="text-sm text-gray-700">
                Recording required
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-6 pt-4 border-t">
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Schedule Meeting
          </button>
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
